import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button, EmptyState, ErrorState, LoadingState, SegmentedControl, Tooltip,
} from '../ui';
import useTrendAnalysis from '../../hooks/useTrendAnalysis';
import {
    SALES_TM_TIP, SALES_TM_VARIANT_TIP, SALES_YOY_TIP, computeEffectiveKpis, formatCurrency, lastCompleteYear,
    rollingTrend, salesTmMonth, totalTrend,
} from '../../utils/categoryTrends';
import { LineChart, YoyBars } from './charts';
import { KpiTile, TrendBadge } from './bits';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Line tones for the years of the sales chart, newest first.
const YEAR_TONES = ['ink', 'sage', 'slate', 'amber', 'teal'];

/** The six KPI tiles (P-BR-105..109), today's labels and formulas. */
function TrendKpis({ kpis, trendsData, selectedVariant }) {
    if (!kpis) return null;
    const suffix = selectedVariant ? ` (${selectedVariant})` : '';
    const yoy = kpis.sales_yoy_pct;
    const month = salesTmMonth(trendsData, selectedVariant);
    const trend = kpis.market_trend;
    return (
        <section className="br2-ctk" aria-label="Trend figures">
            <KpiTile
                testId="kpi-sales-yoy"
                label="Sales YoY"
                tip={SALES_YOY_TIP}
                value={yoy == null ? '—' : (
                    <><span className="br2-arrow" aria-hidden="true">{yoy < 100 ? '▼' : '▲'}</span>{yoy.toFixed(1)}%</>
                )}
                valueClass={yoy == null ? undefined : (yoy < 100 ? 'br2-ink-crit' : 'br2-ink-good')}
                sub="vs the same days last year"
            />
            <KpiTile
                testId="kpi-sales-tm"
                label="Sales TM"
                chip={month ? month.short : null}
                tip={selectedVariant ? SALES_TM_VARIANT_TIP : `${SALES_TM_TIP}${month ? ` (${month.long})` : ''}.`}
                value={formatCurrency(kpis.sales_tm)}
                sub={month ? `${month.long}${selectedVariant ? '' : ' to date · used'}` : null}
            />
            <KpiTile
                testId="kpi-trend"
                label={`Market Trend${suffix}`}
                tip="Uptrend when 6-month rolling avg price > 12-month avg. Downtrend when below."
                value={trend ? <TrendBadge trend={trend} /> : '—'}
                valueClass="br2-ctk-badge"
                sub="6M vs 12M average"
            />
            {[['3M', 3, kpis.rolling_3m_avg], ['6M', 6, kpis.rolling_6m_avg], ['12M', 12, kpis.rolling_12m_avg]].map(([k, m, v]) => (
                <KpiTile
                    key={k}
                    testId={`kpi-${k}`}
                    label={`${k} Avg${suffix}`}
                    tip={`Trailing ${m}-month average sale price (as of latest data month)`}
                    value={formatCurrency(v)}
                    sub="net sale price"
                />
            ))}
        </section>
    );
}

TrendKpis.propTypes = { kpis: PropTypes.object, trendsData: PropTypes.object, selectedVariant: PropTypes.string };

/** AI trend analysis (P-BR-110..114): today's states, expanded after a load. */
function AnalysisCard() {
    const {
        analysis, loading, error, collapsed, setCollapsed, lines, refresh,
    } = useTrendAnalysis();
    if (loading && !analysis) {
        return (
            <section className="br2-analysis" aria-label="AI Trend Analysis">
                <h3>AI Trend Analysis</h3>
                <p className="br2-analysis-wait" role="status">Analyzing moving average crossovers...</p>
            </section>
        );
    }
    if ((error && !analysis) || !analysis) return null;
    const first = lines.slice(0, 2);
    return (
        <section className="br2-analysis" aria-label="AI Trend Analysis" data-testid="analysis">
            <div className="br2-row">
                <h3>AI Trend Analysis</h3>
                {analysis.generated_at && (
                    <span className="br2-sub">{new Date(analysis.generated_at).toLocaleDateString()}</span>
                )}
                <span className="br2-spacer" />
                <button type="button" className="v2-clear" onClick={refresh} disabled={loading}>
                    {loading ? 'Analyzing...' : 'Refresh'}
                </button>
                <Button size="sm" aria-expanded={!collapsed} onClick={() => setCollapsed((c) => !c)}>
                    {collapsed ? 'Expand' : 'Collapse'}
                </Button>
            </div>
            {collapsed ? (
                <p>
                    {first.join(' ')}
                    {lines.length > 2 && (
                        <>
                            {' '}
                            <button type="button" className="v2-btn v2-btn--link br2-more" onClick={() => setCollapsed(false)}>...show more</button>
                        </>
                    )}
                </p>
            ) : (
                // eslint-disable-next-line react/no-array-index-key
                lines.map((line, i) => <p key={i}>{line}</p>)
            )}
        </section>
    );
}

/**
 * Variant list (P-BR-115..122): the Group ▾ filter, expand (a real button,
 * it does not select), row button selects / deselects the variant, sub rows
 * with their listing category, and the majority Total row.
 */
function VariantList({ variants, selectedVariant, onSelect }) {
    const [expanded, setExpanded] = useState(() => new Set());
    const [group, setGroup] = useState('All');
    const shown = group === 'All' ? variants : variants.filter((v) => v.variant === group);
    const toggle = (name) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });
    return (
        <section className="v2-board br2-board" aria-labelledby="br2-vt-h">
            <div className="br2-card-h">
                <h2 id="br2-vt-h">Variant Trends</h2>
                <span className="br2-spacer" />
                <select className="v2-sel br2-group" aria-label="Group" value={group} onChange={(e) => setGroup(e.target.value)}>
                    <option value="All">Group</option>
                    {variants.map((v) => <option key={v.variant} value={v.variant}>{v.variant}</option>)}
                </select>
            </div>
            <div className="br2-vhead">
                <span>Variant <Tooltip label="About Variant" content="Equipment variant grouping (e.g. TRA = Tractors, CMB = Combines)" /></span>
                <span>Trend <Tooltip label="About Trend" content="Market direction based on 6M vs 12M rolling average sale price per variant" /></span>
            </div>
            <ul className="br2-vlist">
                {shown.map((v) => {
                    const subs = v.subcategories || [];
                    const open = expanded.has(v.variant);
                    return (
                        <li key={v.variant}>
                            <div className="br2-vrow-wrap">
                                {subs.length > 0 ? (
                                    <button
                                        type="button"
                                        className="br2-exp"
                                        aria-expanded={open}
                                        aria-label={`${open ? 'Collapse' : 'Expand'} ${v.variant}`}
                                        onClick={() => toggle(v.variant)}
                                    >
                                        <span aria-hidden="true">{open ? '▼' : '▶'}</span>
                                    </button>
                                ) : <span className="br2-exp" aria-hidden="true" />}
                                <button
                                    type="button"
                                    className="br2-vrow"
                                    aria-pressed={selectedVariant === v.variant}
                                    onClick={() => onSelect(selectedVariant === v.variant ? null : v.variant)}
                                >
                                    <span className="br2-vrow-nm">{v.variant}</span>
                                    <TrendBadge trend={v.market_trend} />
                                </button>
                            </div>
                            {open && (
                                <ul className="br2-vsubs">
                                    {subs.map((s) => (
                                        <li key={s.category} className="br2-vsub">
                                            <span>
                                                {s.category}
                                                {s.mf_category && (
                                                    <Tooltip content="Listing category">
                                                        <span className="br2-mf" tabIndex={0}>{s.mf_category}</span>
                                                    </Tooltip>
                                                )}
                                            </span>
                                            <TrendBadge trend={s.market_trend} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
            <div className="br2-vrow br2-vtotal" data-testid="variant-total">
                <span className="br2-vrow-nm">Total</span>
                <TrendBadge trend={totalTrend(variants)} />
            </div>
        </section>
    );
}

VariantList.propTypes = {
    variants: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedVariant: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
};

const kFmt = (v) => `${Math.round(v / 1000)}K`;

/** Rolling avg (last complete year), rolling YoY bars, monthly sales by year. */
function TrendCharts({ trendsData, kpis, selectedVariant }) {
    const [mode, setMode] = useState('value');
    const year = lastCompleteYear(trendsData);
    const rollingSource = selectedVariant
        ? (trendsData.rolling_avg_by_month_by_variant || {})[selectedVariant] || []
        : trendsData.rolling_avg_by_month || [];
    const rolling = rollingSource.filter((d) => d.year === year).slice().sort((a, b) => a.month - b.month);
    const salesSource = selectedVariant
        ? (trendsData.sales_by_year_month_by_variant || {})[selectedVariant] || []
        : trendsData.sales_by_year_month || [];
    const years = [...salesSource].sort((a, b) => b.year - a.year);
    const currentYear = years.length ? years[0].year : null;
    const trend = rollingTrend(rolling);
    const suffix = selectedVariant ? ` — ${selectedVariant}` : '';
    const units = mode === 'units';
    const salesTitle = `${units ? 'Monthly Units Sold by Year' : 'Monthly Sales Value by Year ($)'}${suffix}`;

    return (
        <div className="br2-charts">
            <section className="v2-board br2-board" aria-labelledby="br2-ra-h">
                <div className="br2-card-h">
                    <h2 id="br2-ra-h">Rolling Avg. Net Sale Price{suffix}</h2>
                    <Tooltip
                        label="About the rolling average"
                        content="Average net sale price over trailing 3, 6, and 12 month windows. When the 3M line is above the 12M line, prices are rising (uptrend). When below, prices are falling (downtrend)."
                    />
                    <span className="br2-sub">{year}</span>
                    {trend && (
                        <span className={`br2-rtrend ${trend === 'Uptrend' ? 'br2-ink-good' : 'br2-ink-crit'}`} data-testid="rolling-trend">
                            {trend === 'Uptrend' ? '▲ Uptrend' : '▼ Downtrend'}
                        </span>
                    )}
                </div>
                {rolling.length ? (
                    <LineChart
                        label={`Rolling average net sale price ${year}${suffix}`}
                        labels={rolling.map((d) => MONTHS[d.month - 1])}
                        series={[
                            { name: '3M', tone: 'ink', values: rolling.map((d) => d.avg_3m ?? null) },
                            { name: '6M', tone: 'sage', values: rolling.map((d) => d.avg_6m ?? null) },
                            { name: '12M', tone: 'slate', dashed: true, values: rolling.map((d) => d.avg_12m ?? null) },
                        ]}
                        formatY={kFmt}
                        formatTip={kFmt}
                        tipTitle={(i) => `${MONTHS[rolling[i].month - 1]} ${year}`}
                    />
                ) : <p className="br2-chart-empty">No data</p>}
                <div className="br2-legend br2-legend--pad">
                    <span><i className="br2-ln-key br2-ln-key--ink" aria-hidden="true" />3M</span>
                    <span><i className="br2-ln-key br2-ln-key--sage" aria-hidden="true" />6M</span>
                    <span><i className="br2-ln-key br2-ln-key--slate is-dashed" aria-hidden="true" />12M</span>
                </div>
            </section>
            <section className="v2-board br2-board" aria-labelledby="br2-yoy-h">
                <div className="br2-card-h">
                    <h2 id="br2-yoy-h">Rolling Avg. Price YoY %</h2>
                    <Tooltip
                        label="About rolling price YoY"
                        content="Year-over-year percentage change in rolling average sale prices. Positive = prices rising vs last year. Negative = prices falling."
                    />
                    <span className="br2-sub">vs same window last year</span>
                </div>
                <div className="br2-chart-pad">
                    <YoyBars
                        labels={['3M', '6M', '12M']}
                        values={[kpis?.rolling_3m_yoy ?? null, kpis?.rolling_6m_yoy ?? null, kpis?.rolling_12m_yoy ?? null]}
                    />
                </div>
            </section>
            <section className="v2-board br2-board br2-wide" aria-labelledby="br2-sales-h">
                <div className="br2-card-h">
                    <h2 id="br2-sales-h">{salesTitle}</h2>
                    <Tooltip
                        label="About monthly sales"
                        content={units
                            ? 'Number of units sold per month, compared across years. Shows volume trends independent of price changes.'
                            : 'Total net sales value (sale price minus overallowance) per month, compared across years. Shows seasonal patterns and year-over-year volume trends.'}
                    />
                    <span className="br2-spacer" />
                    <SegmentedControl
                        label="Sales measure"
                        value={mode}
                        onChange={setMode}
                        options={[{ value: 'value', label: 'Value ($)' }, { value: 'units', label: 'Units' }]}
                    />
                </div>
                {years.length ? (
                    <LineChart
                        label={salesTitle}
                        labels={MONTHS}
                        fromZero
                        width={960}
                        height={300}
                        series={years.map((y, i) => ({
                            name: String(y.year),
                            tone: YEAR_TONES[i] || 'slate',
                            emphasis: y.year === currentYear,
                            dashed: i >= 2,
                            values: (units ? y.monthly_units : y.monthly_sales) || [],
                        }))}
                        formatY={units ? (v) => v.toLocaleString('en-US') : (v) => `$${(v / 1_000_000).toFixed(1)}M`}
                        formatTip={units ? (v) => `${v.toLocaleString('en-US')} units` : formatCurrency}
                    />
                ) : <p className="br2-chart-empty">No data</p>}
                <div className="br2-legend br2-legend--pad">
                    {years.map((y, i) => (
                        <span key={y.year}><i className={`br2-ln-key br2-ln-key--${YEAR_TONES[i] || 'slate'}${i >= 2 ? ' is-dashed' : ''}`} aria-hidden="true" />{y.year}</span>
                    ))}
                </div>
                <p className="br2-cnote">New and used combined.</p>
            </section>
        </div>
    );
}

TrendCharts.propTypes = { trendsData: PropTypes.object.isRequired, kpis: PropTypes.object, selectedVariant: PropTypes.string };

/** The Trend sub-view: states, KPIs, analysis, variants and charts. */
export default function TrendViewV2({
    trendsData, loading, error, onRetry,
}) {
    const [selectedVariant, setSelectedVariant] = useState(null);
    const kpis = useMemo(() => computeEffectiveKpis(trendsData, selectedVariant), [trendsData, selectedVariant]);

    if (loading) return <LoadingState label="Loading trend data..." />;
    if (error) return <ErrorState message={error} onRetry={onRetry} />;
    if (!trendsData) return <EmptyState message="No data loaded yet." />;

    const variants = trendsData.variant_trends || [];
    return (
        <div className="br2-panel">
            <TrendKpis kpis={kpis} trendsData={trendsData} selectedVariant={selectedVariant} />
            <AnalysisCard />
            {variants.length === 0 ? (
                <EmptyState message="No variant data available" />
            ) : (
                <div className="br2-ctl2">
                    <VariantList variants={variants} selectedVariant={selectedVariant} onSelect={setSelectedVariant} />
                    <TrendCharts trendsData={trendsData} kpis={kpis} selectedVariant={selectedVariant} />
                </div>
            )}
        </div>
    );
}

TrendViewV2.propTypes = {
    trendsData: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRetry: PropTypes.func,
};
